import { useEffect, useRef, useState, type ReactNode } from "react";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Search } from "lucide-react";
import { useLanguage } from "@/shared/hooks/use-language";
import { UserCardSkeleton } from "@/shared/components/UserCardSkeleton";
import type { UserAccess } from "@/features/auth/api/user-access.ts";
import type { AccessListState } from "@/features/auth/model/use-access-listing";

function matchesQuery(access: UserAccess, query: string): boolean {
  return (access.user_name || "").toLowerCase().includes(query)
    || (access.user_reference_id || "").toLowerCase().includes(query)
    || access.user_id.toLowerCase().includes(query)
    || access.user_type.toLowerCase().includes(query)
    || access.roles.some(role => (role.display_name || role.name).toLowerCase().includes(query));
}

export interface AccessListProps {
  state: AccessListState;
  // What to say when the listing is known complete and holds nothing. It is a claim about a
  // scope and a filter set, so only the screen that chose them can word it.
  emptyMessage: string;
  // Overridden where the rows are one kind of user rather than every kind -- the
  // Integrations screen searches integrations, not "users".
  searchPlaceholder?: string;
  onRetry: () => void;
  onLoadMore: (loadEverything: boolean) => void;
  onRetryLoadMore: () => void;
  onContinue: () => void;
  renderCard: (access: UserAccess) => ReactNode;
}

/**
 * The listing itself: the search box over it, the rows, and every state the cursor can leave
 * it in -- loading, failed, empty, partially loaded, capped.
 *
 * Paired with `useAccessListing`, which owns the same listing's data. Both screens that list
 * User-Accesses render this, so a row means the same thing and a partial listing says so the
 * same way wherever it is read.
 */
export const AccessList = ({
  state,
  emptyMessage,
  searchPlaceholder,
  onRetry,
  onLoadMore,
  onRetryLoadMore,
  onContinue,
  renderCard,
}: AccessListProps) => {
  const { t } = useLanguage();
  // No debounce: the query now only filters rows already in memory. It used to settle
  // before firing a full-listing drain, and that drain is a control the user clicks.
  const [searchQuery, setSearchQuery] = useState("");
  const loadMoreSentinel = useRef<HTMLDivElement | null>(null);

  // More pages exist and nothing has stopped us from asking for them.
  const canAutoLoad = Boolean(
    state.cursor && !state.isLoading && !state.isLoadingMore
    && !state.truncated && !state.loadMoreError
    // A search filters rows already in memory; it must not also decide whether the listing
    // keeps paging. The sentinel renders after the *filtered* rows, so without this a query
    // matching one row left it on screen and drained the listing exactly as before, while a
    // query matching none unmounted it and did not -- the same typed gesture costing 49
    // requests or 0 depending on how many rows happened to match. The drain is the control.
    && !searchQuery
  );

  // Distinct from "loading" only in where it comes from, and distinct from "empty" in every
  // way that matters: a listing nothing has asked for yet has no rows, no cursor and no
  // error, which satisfies every other term below. It renders as the skeleton rather than as
  // a sentence about what the scope contains.
  const notAskedYet = !state.hasAnswered && !state.error;

  // `canAutoLoad` is a dependency because it is what renders the sentinel: the node does
  // not exist on the render where this first runs. Without it the effect happens to re-run
  // anyway -- `loadMore`'s identity changes with the listing state -- and auto-paging would
  // stop silently the day that callback is properly memoised.
  useEffect(() => {
    const sentinel = loadMoreSentinel.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) onLoadMore(false);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canAutoLoad, onLoadMore]);

  // A search still may not answer "no such member" off a partial list -- but it no longer
  // buys that answer by draining the whole listing on every settled keystroke. One typed
  // query used to cost up to MAX_PAGES sequential round-trips (measured: 49 requests, 490
  // rows), and again per scope, on a screen that previously issued none of them.
  //
  // So the honesty and the cost are separated. The search filters what is loaded; when it
  // finds nothing in an *incomplete* listing the screen says exactly that rather than "no
  // results", and offers the drain as a control the user chooses. The claim the drain
  // existed to prevent is still never made: only `listingComplete` licenses "no results".
  const searchTheRest = () => onLoadMore(true);

  // Retry and Continue only clear a blocking flag (`loadMoreError`, `truncated`) in the
  // hook; the actual page fetch was always expected to follow from `canAutoLoad` picking
  // the drain back up once the flag was gone. `canAutoLoad` also requires `!searchQuery`,
  // which the hook cannot see or clear -- so under a search, clicking either button removed
  // the incompleteness notice and asked for nothing at all: the same shape `canAutoLoad`'s
  // own `!searchQuery` term was added to fix for the sentinel, left open on these two.
  // Calling `searchTheRest` here closes it the same way the "search the rest" control
  // already does for the no-match and one-match branches -- a real drain, not reliance on
  // a render effect that a search query permanently suppresses.
  const retryAndResume = () => {
    onRetryLoadMore();
    if (searchQuery) searchTheRest();
  };

  const continueAndResume = () => {
    onContinue();
    if (searchQuery) searchTheRest();
  };

  const visibleAccesses = searchQuery
    ? state.accesses.filter(access => matchesQuery(access, searchQuery.toLowerCase()))
    : state.accesses;

  // "Nobody has access" may only be said once the listing is known complete, and an
  // outstanding cursor means it is not -- whether we stopped because a page is in flight,
  // because one failed, or because the page cap was reached. Any of those and the honest
  // answer is that we do not know yet.
  // `truncated` is named as its own term even though it now always carries a live cursor --
  // the page budget and an echoed cursor both keep the cursor rather than clear it (see
  // use-access-listing.ts), specifically so Continue at :265 always has one to act on. That
  // makes `!state.cursor` already false whenever `truncated` is true, so this term is not
  // load-bearing today; it stays as an explicit statement of the invariant rather than an
  // implication a future reader would have to re-derive.
  // `hasAnswered` is the first term deliberately: a listing that was never asked has no
  // rows and no cursor, so every other term is satisfied and the screen would report the
  // scope empty on the strength of a request that never went out.
  const listingComplete = state.hasAnswered && !state.isLoadingMore && !state.cursor
    && !state.truncated;

  return (
    <>
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            aria-label={searchPlaceholder ?? t("access.searchPlaceholder")}
            placeholder={searchPlaceholder ?? t("access.searchPlaceholder")}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
      </div>

      <div className="mb-8">
        {state.isLoading || notAskedYet ? (
          <div className="space-y-4">
            <UserCardSkeleton />
          </div>
        ) : state.error ? (
          <Card className="bg-card border-border p-6">
            <div className="py-8">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm text-destructive mb-3" data-testid="access-error">
                  {state.error}
                </p>
                <Button variant="outline" size="sm" onClick={onRetry} data-testid="access-retry">
                  {t("access.retry")}
                </Button>
              </div>
            </div>
          </Card>
        ) : visibleAccesses.length === 0 ? (
          // "Nobody has access" is a claim about the account, so it may only render once
          // the listing is known complete. While a search is still draining the cursor
          // the honest answer is that we do not know yet.
          <Card className="bg-card border-border p-6">
            {listingComplete ? (
              <div className="text-center py-8" data-testid="access-empty">
                <p className="text-muted-foreground">
                  {searchQuery ? t("access.noResults") : emptyMessage}
                </p>
                {searchQuery && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setSearchQuery("")}
                    data-testid="access-clear-search"
                  >
                    {t("access.clearSearch")}
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8" data-testid="access-incomplete">
                <p className="text-muted-foreground">
                  {state.isLoadingMore
                    ? t("access.stillLoading")
                    : searchQuery
                    ? t("access.noResultsSoFar")
                    : t("access.notFullyLoaded")}
                </p>
                {searchQuery && !state.isLoadingMore && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={searchTheRest}
                    data-testid="access-search-rest"
                  >
                    {t("access.searchTheRest")}
                  </Button>
                )}
              </div>
            )}
          </Card>
        ) : (
          <>
            <div className="space-y-4 animate-fadeIn">
              {visibleAccesses.map(access => renderCard(access))}
            </div>
            {canAutoLoad && <div ref={loadMoreSentinel} />}
            {searchQuery && !listingComplete && !state.isLoadingMore && (
              // The same control the no-match branch offers. Matches found so far are not
              // the whole answer while the listing is incomplete, and the user has no other
              // way to reach the rest once the sentinel is suppressed for the search.
              <div className="mt-4 text-center" data-testid="access-search-more">
                <p className="text-sm text-muted-foreground mb-3">
                  {t("access.moreMayMatch")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={searchTheRest}
                  data-testid="access-search-rest"
                >
                  {t("access.searchTheRest")}
                </Button>
              </div>
            )}
          </>
        )}

        {state.isLoadingMore && (
          <p
            className="mt-4 text-sm text-muted-foreground"
            data-testid="access-loading-more"
            role="status"
            aria-live="polite"
          >
            {t("access.loadingMore")}
          </p>
        )}

        {state.loadMoreError && (
          <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-destructive mb-3" data-testid="access-load-more-error">
              {state.loadMoreError}
            </p>
            {/* Gated on `isLoadingMore` like `access-search-more` above: `retryAndResume`
                clears `loadMoreError` unconditionally, and a drain already in flight (started
                elsewhere -- "Search the rest" ignores this same flag by design) leaves this
                message rendered under a request its own click never issued. A click here mid-
                drain used to clear the notice for a failure that request had not caused, and
                once the drain settled its own outcome landed on top regardless. */}
            {!state.isLoadingMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={retryAndResume}
                data-testid="access-load-more-retry"
              >
                {t("access.retry")}
              </Button>
            )}
          </div>
        )}

        {canAutoLoad && (
          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={() => onLoadMore(false)}
            data-testid="access-load-more"
          >
            {t("access.loadMore")}
          </Button>
        )}

        {state.truncated && (
          <div className="mt-4" data-testid="access-truncated">
            <p className="text-sm text-muted-foreground">{t("access.truncated")}</p>
            {/* The notice above stays up regardless -- it is still true that the listing is
                incomplete while a drain runs. Only the button that acts on it is gated, the
                same way `access-search-more`'s is: `continueAndResume` resets `truncated` and
                `pagesLoaded` unconditionally, and a drain already in flight (started by
                "Search the rest", which does not touch this flag) leaves `truncated` and
                `cursor` exactly as they were until it settles -- so this button used to stay
                clickable for the whole drain. A click there mid-drain reset the budget under a
                request already running; if that request then failed, the catch only patches
                `isLoadingMore`/`loadMoreError`, stranding the listing at `pagesLoaded: 0` with
                a live cursor and no cap ever reached again. */}
            {state.cursor && !state.isLoadingMore && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={continueAndResume}
                data-testid="access-continue"
              >
                {t("access.continueLoading")}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
